/**
 * Enterprise-integration view types (PB-1590 / EI-02).
 *
 * Mirrors `docs/api/playbook-be/domains/integration/schemas.yaml`, which is
 * authoritative — if the two disagree, fix this file, never the contract.
 *
 * Types only, following the `document-analysis` precedent: each feature package
 * layers its own domain client and mock on top. No fetch helpers here, and none
 * are useful yet — EI-02 declares one read-only path and the administrator,
 * connection and execution routes arrive with their controllers in EI-03,
 * EI-04 and EI-05.
 *
 * PROVIDER-NEUTRAL. No provider name appears in any type here, and none may be
 * added: the whole bet is that a new provider costs one connector package and
 * zero core change, which stops being true the moment a shared type says
 * "jira". Provider variety travels as validated JSON, not as field names.
 *
 * Nothing here carries credential material. The API never returns access
 * tokens, refresh tokens, API keys or secret-manager values — not even a
 * secret reference — so `TenantIntegration` exposes only whether a client
 * secret is configured, as a boolean.
 */

import { REMOTE_TOOL_DEFECTS } from "@playbookai/integration-kernel";

export type IntegrationProtocol = "mcp" | "rest";

export type IntegrationAuthMode =
  | "delegated_oauth"
  | "tenant_shared"
  | "service_account"
  | "app_installation";

export type IntegrationConnectionMode =
  | "delegated_user"
  | "tenant_shared"
  | "mixed";

export type PlayerAiDefaultToolAccess = "all_enabled" | "read_only";

export type IntegrationSubjectType = "user" | "tenant" | "service_account";

export type IntegrationCredentialType =
  | "oauth"
  | "api_token"
  | "service_principal"
  | "app_installation";

export type IntegrationConnectionStatus =
  | "pending"
  | "active"
  | "expired"
  | "revoked"
  | "failed";

export type IntegrationTemplateStatus = "available" | "disabled" | "deprecated";

/**
 * Durable execution state.
 *
 * `reconciliation_required` means the provider write MAY have landed and the
 * platform cannot tell. It is never reached by retry — retrying an ambiguous
 * write is how duplicate external objects get created — so a UI should offer
 * "check again" or human resolution, never "try again".
 */
export type IntegrationExecutionStatus =
  | "prepared"
  | "approved"
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "reconciliation_required";

/** States a user can still act on; everything else is terminal for now. */
export const ACTIVE_INTEGRATION_EXECUTION_STATUSES: ReadonlySet<IntegrationExecutionStatus> =
  new Set(["prepared", "approved", "queued", "running"]);

/**
 * Needs a person, not a retry. Separate from the failed set on purpose: the
 * affordance differs, and conflating them is what produces duplicates.
 */
export const RECONCILING_INTEGRATION_EXECUTION_STATUSES: ReadonlySet<IntegrationExecutionStatus> =
  new Set(["reconciliation_required"]);

/**
 * The closed set of sanitized codes an integration error may carry. A
 * provider's own message is never surfaced — it routinely contains account
 * names, internal URLs and occasionally credential fragments.
 */
export type IntegrationErrorCode =
  | "CAPABILITY_NOT_FOUND"
  | "OPERATION_NOT_ALLOWED"
  | "CREDENTIAL_REJECTED"
  | "CREDENTIAL_UNAVAILABLE"
  | "PROVIDER_FORBIDDEN"
  | "PROVIDER_NOT_FOUND"
  | "PROVIDER_REJECTED_INPUT"
  | "PROVIDER_RATE_LIMITED"
  | "PROVIDER_UNAVAILABLE"
  | "OUTCOME_AMBIGUOUS"
  | "CONNECTOR_CONTRACT_VIOLATION"
  // The administrator's integration configuration is unusable or
  // self-contradictory — nothing was attempted, and only a human editing the
  // integration resolves it.
  | "INTEGRATION_MISCONFIGURED"
  | "INTEGRATION_INTERNAL_ERROR"
  // PB-4542 — the instance is enabled, but this user holds no grant on it.
  // Added to match the contract, which has declared it since that ticket
  // (`docs/api/.../integration/schemas.yaml`, `IntegrationErrorCode`). The
  // union is the client's copy of a server-owned enum, so a missing member is
  // not a narrower type — it is a code the server really sends that no
  // exhaustive `switch` here can name.
  | "INTEGRATION_NOT_GRANTED"
  // PB-4734 (G4) — the approver left the tenant; the drain refused the queued
  // work terminally. Same name as the HTTP path's refusal for the same fact.
  | "INTEGRATION_MEMBERSHIP_INACTIVE";

/** Re-authorizing fixes these; nothing else will. */
export const REAUTHORIZE_INTEGRATION_ERROR_CODES: ReadonlySet<IntegrationErrorCode> =
  new Set(["CREDENTIAL_REJECTED", "CREDENTIAL_UNAVAILABLE"]);

/**
 * `<product>.<object>.<verb>` with verbs from a closed set. Kept as a string
 * rather than a union: the products and objects come from connector manifests,
 * so enumerating them here would reintroduce the provider knowledge the
 * architecture removes.
 */
export type IntegrationCapabilityKey = string;

export interface IntegrationTemplate {
  readonly id: string;
  readonly templateKey: string;
  /** Provider FAMILY, not a product. An extensible string, not a closed set. */
  readonly providerKey: string;
  readonly displayName: string;
  readonly protocol: IntegrationProtocol;
  readonly supportedAuthModes: readonly IntegrationAuthMode[];
  /** Manifest version — bumps on manifest change, not on deploy. */
  readonly version: number;
  readonly status: IntegrationTemplateStatus;
}

export interface TenantIntegration {
  readonly id: string;
  readonly templateId: string;
  readonly displayName: string;
  readonly description: string | null;
  /** Disabling blocks new executions and preserves history. There is no delete. */
  readonly enabled: boolean;
  readonly connectionMode: IntegrationConnectionMode;
  readonly externalTenantId: string | null;
  readonly playerAiDefaultAccess: PlayerAiDefaultToolAccess;
  /**
   * Whether a LEGACY, pre-PB-4734 client-secret reference is still on the row.
   *
   * No longer an indicator of whether the integration can authorize: the OAuth
   * application is the platform's, so a perfectly working integration reports
   * `false` here. Nothing in the UI reads it, and it is removed together with
   * the column once every environment reads zero non-null rows (plan §7.5).
   */
  readonly hasClientSecret: boolean;
}

export interface IntegrationConnection {
  readonly id: string;
  readonly tenantIntegrationId: string;
  readonly subjectType: IntegrationSubjectType;
  readonly subjectId: string | null;
  readonly displayName: string;
  readonly credentialType: IntegrationCredentialType;
  readonly externalPrincipalName: string | null;
  /** A string, not a uuid — some providers return a prefixed composite. */
  readonly externalPrincipalId: string | null;
  readonly status: IntegrationConnectionStatus;
  /**
   * Which transport this connection's governed work routes over.
   *
   * Every connection is created `rest` and stays there until its owner
   * authorizes MCP and the platform proves the credential works. A connection
   * still reading `rest` after a tenant enables MCP has lost nothing — its
   * existing actions keep working — so offer "authorize MCP", never "reconnect".
   */
  readonly transportMode: IntegrationTransportMode;
  /** When MCP was last proved to work. Null means it never has. */
  readonly mcpValidatedAt: string | null;
  readonly playerAiToolAccessMode: PlayerAiToolAccessMode;
  readonly lastVerifiedAt: string | null;
}

export type IntegrationTransportMode = "rest" | "mcp";

export type PlayerAiToolAccessMode =
  | "workspace_default"
  | "read_only"
  | "custom";

export interface IntegrationResourceDescriptor {
  readonly resourceType: string;
  readonly id: string;
  readonly displayName: string;
  /** Set when the manifest declares a hierarchy. */
  readonly parentId: string | null;
}

export type IntegrationFormFieldType =
  | "text"
  | "textarea"
  | "select"
  | "multiselect"
  | "number"
  | "date"
  | "boolean";

export interface IntegrationFormField {
  readonly key: string;
  readonly label: string;
  readonly type: IntegrationFormFieldType;
  readonly required: boolean;
  readonly help?: string;
  readonly options?: readonly { value: string; label: string }[];
  /**
   * Another field's key. This field's options are valid only for a given value
   * of that one, so the UI reloads them when it changes.
   */
  readonly dependsOn?: string;
}

export interface IntegrationFormSpec {
  readonly fields: readonly IntegrationFormField[];
}

/** Exactly what the user approves. Producing it performs no provider write. */
export interface IntegrationPreview {
  readonly title: string;
  readonly fields: readonly { key: string; label: string; value: string }[];
  readonly targetDescription: string;
}

/**
 * One action offered for a source item, built from a manifest plus tenant
 * policy. Render whatever comes back — a UI must not hard-code a list of
 * provider actions.
 */
export interface IntegrationActionDescriptor {
  readonly integrationId: string;
  readonly connectionId: string;
  readonly capabilityKey: IntegrationCapabilityKey;
  /** The instance's administrator-chosen name — WHERE the work goes. */
  readonly displayName: string;
  /**
   * The capability's own name from the connector manifest — WHAT the action
   * does. Both are rendered: one instance can expose several capabilities, and
   * one provider can have several instances, so either name alone leaves the
   * menu ambiguous.
   */
  readonly capabilityDisplayName: string;
  /**
   * The connection's own name, as the user who authorized it typed it. What
   * distinguishes two entries that share an instance and a capability — the
   * deliberate several-connections-per-member case. Selection stays by
   * `connectionId`; this makes that id legible.
   */
  readonly connectionDisplayName: string;
  readonly providerKey: string;
  readonly objectType: string;
  readonly resourceType: string;
  readonly requiresApproval: boolean;
  readonly form?: "static" | "dynamic";
}

export interface IntegrationExecution {
  readonly id: string;
  readonly integrationConnectionId: string;
  readonly productKey?: string;
  readonly capabilityKey: IntegrationCapabilityKey;
  readonly resourceKey: string | null;
  readonly status: IntegrationExecutionStatus;
  readonly lastErrorCode: IntegrationErrorCode | null;
  /** The external reference created on success, if any. */
  readonly externalWorkRefId: string | null;
  readonly correlationId?: string;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

/**
 * The governed remote-tool catalogue (decisions d6, d7 and d11).
 *
 * Two shapes, not one, mirroring the contract. An ADMINISTRATOR sees every entry
 * including quarantined and withdrawn ones, the server's untrusted annotations,
 * and any defect — that is the review. A MEMBER sees the saved catalogue as a
 * read-only availability view, including why a tool is not in effect for their
 * connection.
 *
 * Still provider-neutral: `toolName` is an opaque string a remote server chose,
 * and no type here names a provider or carries a server URL.
 */

/**
 * Where a discovered tool sits in the review cycle.
 *
 * `quarantined` is where a NEW tool and a CHANGED schema both land, and it is
 * distinct from `disabled` because they mean different things: disabled is a
 * decision, quarantined is a decision not yet made. There is deliberately no
 * `discovered` value — a state nobody acted on is one somebody eventually treats
 * as usable.
 */
export type RemoteToolLifecycle =
  | "quarantined"
  | "enabled"
  | "disabled"
  | "withdrawn";

/**
 * The ADMINISTRATOR-CONFIRMED class, which is what governs. Null until
 * confirmed, and null is never callable — a server's own `readOnlyHint`
 * annotation is a suggestion, never a substitute.
 */
export type RemoteRiskClass = "read" | "write";

/**
 * Why an entry can never be enabled, however it is reviewed.
 *
 * DERIVED from the kernel's `REMOTE_TOOL_DEFECTS`, not restated. This was a
 * hand-written mirror until `presentation_policy_mismatch` was added: the
 * kernel, the database CHECK and the OpenAPI response all learned the fourth
 * value while this union did not, so the one place that has to render it — the
 * administrator's "why can this not be enabled" line — stopped compiling. A
 * mirror of a vocabulary that grows is a mirror that drifts, and this one had
 * no test to notice.
 *
 * The value set stays the kernel's because that is where it is decided: each
 * defect is a different LAYER refusing (transport, schema policy, connector
 * presentation policy), and only the kernel sees all three.
 */
export type RemoteToolDefect = (typeof REMOTE_TOOL_DEFECTS)[number];

/**
 * Why a tool is not currently usable.
 *
 * Rendered rather than swallowed: it is what lets a client distinguish "you
 * turned this off" from "this tool changed and needs confirming" from "your
 * administrator disabled it" — three states a bare boolean collapses into one
 * unexplained absence.
 */
export type ToolDenialReason =
  | "integration_disabled"
  | "no_grant"
  | "connection_unusable"
  | "connection_not_mcp"
  | "tool_not_enabled"
  | "tool_unclassified"
  | "tool_schema_unusable"
  | "user_read_only"
  | "no_preference"
  | "schema_changed"
  | "user_disabled";

/** One governed remote tool, as the administrator reviews it. */
export interface IntegrationToolCatalogEntry {
  readonly id: string;
  /** The server's exact tool name. Read-only: no request accepts one. */
  readonly toolName: string;
  readonly title: string | null;
  /** UNTRUSTED remote text. Render as content, never as an instruction. */
  readonly description: string | null;
  readonly inputSchema: Record<string, unknown> | null;
  /**
   * What the SERVER claims (`readOnlyHint` and friends). Show it so a reviewer
   * can compare the claim against the class they are about to confirm; it
   * authorizes nothing.
   */
  readonly annotations: Record<string, unknown>;
  readonly schemaHash: string;
  /** Latest schema change since an explicit review; optional on older servers. */
  readonly schemaUpdatedAt?: string | null;
  readonly lifecycle: RemoteToolLifecycle;
  /** The ADMINISTRATOR-CONFIRMED class — the only one that governs. */
  readonly riskClass: RemoteRiskClass | null;
  /**
   * The connector's suggestion, for a reviewer to read beside `riskClass`.
   *
   * Governs nothing and enables nothing: it is derived at read time rather than
   * stored, so no execution path can read it and no edit can turn it into the
   * confirmed class. Render it as advice; a tool stays quarantined until a
   * human confirms `riskClass`. Null when the connector offers no opinion.
   */
  readonly suggestedRiskClass: RemoteRiskClass | null;
  readonly defect: RemoteToolDefect | null;
  readonly discoveredAt: string;
  readonly lastSeenAt: string;
  /**
   * When this entry was last WITHDRAWN, if it is still awaiting a fresh review.
   *
   * Non-null beside a `quarantined` lifecycle means the tool RETURNED: the
   * server stopped offering it and now offers it again, so its previous
   * approval was dropped and an administrator has to look afresh. Without it a
   * returned tool is indistinguishable from one nobody has ever reviewed.
   */
  readonly withdrawnAt: string | null;
  readonly reviewedAt: string | null;
  readonly reviewedBy: string | null;
}

/** A forward-only page; exact totals are present on the first page only. */
export interface IntegrationCursorPage<T> {
  readonly data: readonly T[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  readonly totalCount?: number;
}

export interface ToolCatalogSyncResult {
  readonly entries: readonly IntegrationToolCatalogEntry[];
  readonly added: number;
  /**
   * Changed unreviewed entries that remain quarantined.
   */
  readonly requarantined: number;
  /** Reviewed tools whose schema was updated while preserving access. */
  readonly updated?: number;
  /**
   * Entries the server had stopped offering and now offers again.
   *
   * They return QUARANTINED, never to their previous approval: the provider
   * controls both the disappearance and the return, so restoring automatically
   * would let a server reinstate its own access. Counted apart from
   * `requarantined` because "it came back" and "it changed shape" are different
   * events for an administrator.
   */
  readonly reappeared: number;
  readonly withdrawn: number;
  /**
   * The sync stopped at its page budget. Surface it — a partial catalogue that
   * looks complete is how an administrator concludes a tool does not exist when
   * it simply was not reached.
   */
  readonly truncated: boolean;
}

/**
 * One governed tool and whether the workspace policy is in effect for this
 * member's connection.
 */
export interface EffectiveIntegrationTool {
  /** The catalogue entry id — what every execution route accepts. */
  readonly id: string;
  readonly toolName: string;
  readonly title: string | null;
  readonly description: string | null;
  readonly inputSchema: Record<string, unknown> | null;
  readonly schemaHash: string;
  readonly riskClass: RemoteRiskClass | null;
  readonly playerAiSelected: boolean;
  readonly workspaceDefaultInEffect: boolean;
  readonly manualInEffect: boolean;
  readonly playerAiInEffect: boolean;
  readonly manualBlockedBy: readonly ToolDenialReason[];
  readonly playerAiBlockedBy: readonly ToolDenialReason[];
}

export interface McpAuthorizationResult {
  readonly id: string;
  readonly transportMode: IntegrationTransportMode;
  readonly mcpValidatedAt: string | null;
}

/**
 * Running a governed tool: the shapes for `/v1/integration-executions/tools/*`.
 *
 * **What these types deliberately CANNOT express is the contract.** There is no
 * `toolName`, no `serverUrl`, no `inputSchema`, no `schemaHash`, no `riskClass`
 * and — most of all — no `surface`. Each is a decision somebody with authority
 * recorded, re-derived server-side from the catalogue entry; a client type that
 * offered one would be inviting a caller to choose which decision applied to
 * them, and the server would refuse the body anyway (`forbidNonWhitelisted`).
 *
 * These routes are the MANUAL surface by construction. Player AI reaches the
 * same administrator-governed catalogue in-process, where no request body can
 * choose a surface.
 */

/** Which kind of operation an execution row is. */
export type IntegrationOperationKind = "capability" | "catalog_tool";

/** Shared by the read and the write-prepare. */
export interface RunCatalogToolRequest {
  /** One of the CALLER's own connections. */
  readonly connectionId: string;
  /** WHICH STORED DECISION to run — not which tool. */
  readonly catalogEntryId: string;
  /**
   * The tool's own named arguments, validated server-side against the EXACT
   * stored schema. `unknown`-valued rather than typed: the shape is the remote
   * server's and changes with each catalogue sync, so anything narrower here
   * would be a guess that goes stale. Build it from
   * `EffectiveIntegrationTool.inputSchema`.
   */
  readonly arguments: Record<string, unknown>;
}

/**
 * The platform item a write comes FROM — BOTH halves or NEITHER, made
 * unrepresentable rather than merely documented.
 *
 * Two optional properties on one interface let a caller write
 * `{ sourceRefId: "P-1" }` and compile. The server refuses it (the DTO's
 * `@ValidateIf` fires in both directions) and the OpenAPI schema refuses it
 * (`dependentRequired`), so a type that accepts it is the one layer that
 * disagrees — and it disagrees at the point where the mistake is cheapest to
 * catch. A name without an id names nothing.
 *
 * The `?: undefined` branch is what makes the union discriminate: without it,
 * the empty-object branch would accept a partial pair by structural
 * assignability.
 */
export type CatalogToolSourceRef =
  | {
      readonly sourceRefName: string;
      readonly sourceRefId: string;
    }
  | {
      readonly sourceRefName?: undefined;
      readonly sourceRefId?: undefined;
    };

export type PrepareCatalogToolWriteRequest = RunCatalogToolRequest &
  CatalogToolSourceRef & {
    /**
     * So a retried prepare returns the ORIGINAL execution. Identity is the
     * caller's own request — connection, entry, canonical arguments, surface
     * and source REF — so a retry still replays after the tool's schema moved
     * or the source item was edited. A key reused for a genuinely DIFFERENT
     * request, including the same one on the other surface, is refused rather
     * than served as a replay.
     */
    readonly idempotencyKey?: string;
    /** Trusted product intent resolved by the contextual-action query. */
    readonly intent?: "create_external_work";
    /** Existing platform Work Link replaced only after the new write succeeds. */
    readonly replacementWorkLinkId?: string;
  };

export type IntegrationContextFormControl =
  | "text"
  | "textarea"
  | "number"
  | "toggle"
  | "select"
  | "items";

export interface IntegrationContextFormField {
  readonly path: string;
  readonly label: string;
  readonly control: IntegrationContextFormControl;
  readonly required: boolean;
  readonly editable: boolean;
  readonly constraints?: {
    readonly min?: number;
    readonly max?: number;
    readonly maxLength?: number;
  };
  readonly options?: readonly {
    readonly value: string;
    readonly label: string;
  }[];
}

/** A connector-authored, governed contextual action. No remote tool prose. */
export interface IntegrationContextActionDescriptor {
  readonly catalogEntryId: string;
  readonly integrationId: string;
  readonly connectionId: string;
  readonly integrationDisplayName: string;
  readonly connectionDisplayName: string;
  readonly intent: "create_external_work";
  readonly serviceKind: string;
  readonly objectType: string;
  readonly title: string;
  readonly form: {
    readonly version: 1;
    readonly fields: readonly IntegrationContextFormField[];
  };
  readonly activeExecution: {
    readonly id: string;
    readonly status: IntegrationExecutionStatus;
    readonly catalogEntryId: string | null;
  } | null;
}

export interface IntegrationContextActionSource {
  readonly sourceRefName: string;
  readonly sourceRefId: string;
  readonly actions: readonly IntegrationContextActionDescriptor[];
}

/** HTTP batch bound shared by callers of the contextual-action query. */
export const MAX_INTEGRATION_CONTEXT_ACTION_SOURCES = 50;

export interface QueryIntegrationContextActionsInput {
  readonly intent: "create_external_work";
  readonly sources: readonly {
    readonly sourceRefName: string;
    readonly sourceRefId: string;
  }[];
}

/**
 * One execution as the detail routes return it, for BOTH kinds of operation.
 *
 * All five discriminated fields are present and nullable rather than optional,
 * so a client always reads a key and never tests for its presence: a
 * `capability` row carries the three capability fields, a `catalog_tool` row
 * carries the two catalogue ones, and the others are null.
 *
 * Distinct from `IntegrationExecution` above, which is the durable-ROW summary
 * vocabulary (`integrationConnectionId`, `resourceKey`, `externalWorkRefId`) —
 * the two are not interchangeable and the contract keeps them apart for the
 * same reason.
 */
export interface CatalogIntegrationExecutionDetail {
  readonly id: string;
  readonly connectionId: string;
  readonly operationKind: IntegrationOperationKind;
  readonly capabilityKey: IntegrationCapabilityKey | null;
  readonly resourceType: string | null;
  readonly resourceId: string | null;
  readonly catalogEntryId: string | null;
  /** The schema hash this execution's approval binds to. */
  readonly toolSchemaHash: string | null;
  readonly sourceRefName: string | null;
  readonly sourceRefId: string | null;
  readonly intent: string | null;
  readonly status: IntegrationExecutionStatus;
  /** What the user approves. Never the raw provider payload. */
  readonly preview: Record<string, unknown> | null;
  readonly result: {
    readonly externalId: string;
    readonly externalKey: string;
    readonly url: string;
    readonly duplicate: boolean;
  } | null;
  readonly lastErrorCode: IntegrationErrorCode | null;
  readonly attemptCount: number;
  readonly nextAttemptAt: string | null;
  readonly approvedAt: string | null;
  readonly correlationId: string;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

/** What an immediate governed READ returns. */
export interface CatalogToolReadResult {
  readonly execution: CatalogIntegrationExecutionDetail;
  /**
   * The tool's answer — the transport's bounded MCP content, and NOT a
   * connector-adapted one. Result adapters run on the WRITE path only, to
   * produce the external reference a Work Link is built from.
   *
   * There is exactly ONE route to this value. A governed MCP read executes over
   * the selected MCP connection or it fails (ADR-0070); it never falls back to,
   * or is answered by, a REST integration, which is a separate template with
   * separate credentials.
   *
   * Untyped for the same reason `arguments` is: the shape belongs to the tool.
   * A FAILED read never carries one; it is a 422.
   *
   * **This is the only place the answer appears.** The execution row retains
   * outcome metadata only, so re-reading the execution will not return it.
   */
  readonly result: unknown;
}
